// Resources types for React Native admin app
// Based on admin-web resources implementation

export { ICommunityResourceFile } from '@/types/shared/resources';

export interface ICommunityResource {
  id: number;
  name: string;
  description: string;
  content: string;
  icon: string;
  url: string;
  propertyId: number;
  position: number;
  files: ICommunityResourceFile[];
}

export interface IResourceResponse {
  success: boolean;
  response: ICommunityResource[];
}

export interface IResourceFormValues {
  name: string;
  description: string;
  content: string;
  icon: string;
  url: string;
  position: number;
  files: IFile[];
  [key: string]: string | number | IFile[];
}

// React Native File part used when appending to FormData
export type RNFilePart = {
  uri: string;
  type: string;
  name: string;
};

export interface IFile {
  id?: number | string;
  file?: RNFilePart; // React Native FormData file part
  name: string;
  size: number;
  type: string;
  uri?: string; // For React Native file handling / preview
}

// Resource icon mappings (matching admin-web)
export type ResioIconOptionMappings = {
  addressBookIcon: string;
  aidIcon: string;
  alarmIcon: string;
  backpackIcon: string;
  bedIcon: string;
  bicycleIcon: string;
  boardIcon: string;
  bookmarkIcon: string;
  bookOpenIcon: string;
  buildingIcon: string;
  booksIcon: string;
  bottleIcon: string;
  briefcaseIcon: string;
  cakeIcon: string;
  calendarIcon: string;
  carIcon: string;
  catIcon: string;
  cigaretteIcon: string;
  coffeeIcon: string;
  cubeIcon: string;
  deliveryIcon: string;
  devicesIcon: string;
  dollarIcon: string;
  doorIcon: string;
  entranceIcon: string;
  fileIcon: string;
  fireIcon: string;
  flagIcon: string;
  folderIcon: string;
  gearIcon: string;
  globeIcon: string;
  idCardIcon: string;
  keyIcon: string;
  maskIcon: string;
  moonIcon: string;
  musicIcon: string;
  parkingIcon: string;
  photoIcon: string;
  printerIcon: string;
  sirenIcon: string;
  stackIcon: string;
  swimmingPoolIcon: string;
  trophyIcon: string;
  warningIcon: string;
  wifiBubbleIcon: string;
  windIcon: string;
  wrenchIcon: string;
};

// Resource constants (matching admin-web)
export const PRIMARY_POSITIONS = [0, 1, 2, 3];
export const ROW_SIZE = 4;
export const NUM_PRIMARY_SLOTS = 4;

export const SECONDARY_POSITIONS = (numSlots: number) => {
  const slots = [];
  let i = 4;
  while (i < numSlots + 1) {
    slots.push(i);
    i += 1;
  }
  return slots;
};

// Resource icon options (matching admin-web)
export const resioIconOptionMappings: ResioIconOptionMappings = {
  addressBookIcon: "Address Book",
  aidIcon: "Aid",
  alarmIcon: "Alarm",
  backpackIcon: "Backpack",
  bedIcon: "Bed",
  bicycleIcon: "Bicycle",
  boardIcon: "Board",
  bookmarkIcon: "Bookmark",
  bookOpenIcon: "Book Open",
  buildingIcon: "Building",
  booksIcon: "Books",
  bottleIcon: "Bottle",
  briefcaseIcon: "Briefcase",
  cakeIcon: "Cake",
  calendarIcon: "Calendar",
  carIcon: "Car",
  catIcon: "Cat",
  cigaretteIcon: "Cigarette",
  coffeeIcon: "Coffee",
  cubeIcon: "Cube",
  deliveryIcon: "Delivery",
  devicesIcon: "Devices",
  dollarIcon: "Dollar",
  doorIcon: "Door",
  entranceIcon: "Entrance",
  fileIcon: "File",
  fireIcon: "Fire",
  flagIcon: "Flag",
  folderIcon: "Folder",
  gearIcon: "Gear",
  globeIcon: "Globe",
  idCardIcon: "ID Card",
  keyIcon: "Key",
  maskIcon: "Mask",
  moonIcon: "Moon",
  musicIcon: "Music",
  parkingIcon: "Parking",
  photoIcon: "Photo",
  printerIcon: "Printer",
  sirenIcon: "Siren",
  stackIcon: "Stack",
  swimmingPoolIcon: "Swimming Pool",
  trophyIcon: "Trophy",
  warningIcon: "Warning",
  wifiBubbleIcon: "Wifi",
  windIcon: "Wind",
  wrenchIcon: "Wrench",
};

// Mobile-specific resource types
export interface IResourceTileProps {
  position: number;
  resource?: ICommunityResource;
  onResourcePress?: (resource: ICommunityResource) => void;
  onEditResource?: (resource: ICommunityResource) => void;
  isEditable?: boolean;
}

export interface IResourceCardProps {
  resource: ICommunityResource;
  onPress?: (resource: ICommunityResource) => void;
  onEdit?: (resource: ICommunityResource) => void;
  showEditButton?: boolean;
}

// Resource grid layout types for mobile
export interface IResourceGridProps {
  resources: ICommunityResource[];
  onResourcePress?: (resource: ICommunityResource) => void;
  onEditResource?: (resource: ICommunityResource) => void;
  onCreateResource?: () => void;
  isEditable?: boolean;
}
